import { useEffect, useState } from "react";
import { EtapaPadrao, useDeleteEtapaPadraoMutation, usePostEtapaPadraoMutation, useUpdateEtapaPadraoMutation } from "shared/features/api/etapaPadrao/etapaPadraoSlice"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { SortableTableRow } from "components/SortableTableRow";
import { FormModal } from "components/FormModal";
import { Etapa, useGetEtapasQuery } from "shared/features/api/etapa/etapaSlice";
import { toast } from "react-toastify";
import { DeleteModal } from "components/DeleteModal";

interface EtapasPadraoTableProps {
  etapas: EtapaPadrao[];
}

export const EtapaPadraoTable: React.FC<EtapasPadraoTableProps> = ({etapas}) => {
    const [open, setOpen] = useState<boolean>(false);
    const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
    const [etapasSelect, setEtapasSelect] = useState<Etapa[]>([]);
    const { data: todasEtapas } = useGetEtapasQuery(); 
    const [etapaSelecionada, setEtapaSelecionada] = useState<number>(0);
    const [postEtapaPadrao] = usePostEtapaPadraoMutation();
    const [deleteEtapaPadrao] = useDeleteEtapaPadraoMutation();
    const [updateEtapaPadrao] = useUpdateEtapaPadraoMutation();
    const [idEtapaPadrao, setIdEtapaPadrao] = useState<string>("");


    useEffect(() => {
    if (todasEtapas && etapas) {
        const usadasIds = etapas.map((e) => e.idEtapa);
        const disponiveis = todasEtapas.filter((etapa) => !usadasIds.includes(etapa.idEtapa));
        setEtapasSelect(disponiveis);
    }
    }, [todasEtapas, etapas]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = etapas.findIndex((item) => item.idEtapa === active.id);
            const newIndex = etapas.findIndex((item) => item.idEtapa === over.id);

            if (oldIndex === -1 || newIndex === -1) return;

            const newItems = [...etapas];
            const [movedItem] = newItems.splice(oldIndex, 1);
            newItems.splice(newIndex, 0, movedItem);

            const updatedItems = newItems.map((item, index) => ({
            ...item,
            ordem: index + 1,
            }));

            try {
                for (const item of updatedItems) {
                    await updateEtapaPadrao({
                    idEtapaPadrao: item.idEtapaPadrao,
                    etapaPadrao: {
                        ordem: item.ordem,
                        peso: item.peso,
                    },
                    }).unwrap();
                }
            } catch (error) {
            console.error("Erro ao atualizar ordem das etapas:", error);
            }
        }
    };


    const handleUpdateWeight = async (id: string, newWeight: number) => {
        const payloadUpdateWeight = {
            idEtapaPadrao: Number(id),
            etapaPadrao: {
                ordem: etapas.find(e => e.idEtapa === Number(id))?.ordem ?? 1,
                peso: newWeight
            }
        }

        await toast.promise(
            updateEtapaPadrao(payloadUpdateWeight).unwrap(),
            {
                pending: "Carregando...",
                success: `Peso atualizado com sucesso!`,
                error: {
                    render({ data }) {
                        const response = data as {
                            data?: {
                            errors?: string[];
                            message?: string;
                            };
                        };

                        const errorMessage =
                            response.data?.errors?.[0] ||
                            response.data?.message ||
                            `Erro ao atualizar peso.`;
                        return errorMessage;
                    },
                },
            }
        ) 
    };

    const handleDelete = (id: string) => {
        setOpenModalDelete(true);
        setIdEtapaPadrao(id);
    }

    const handleConfirmDelete = async (id: string) => {
        await toast.promise(
            deleteEtapaPadrao(Number(id)).unwrap(),
            {
                pending: "Carregando...",
                success: `Etapa padrão removida com sucesso!`,
                error: {
                    render({ data }) {
                        const response = data as {
                            data?: {
                            errors?: string[];
                            message?: string;
                            };
                        };

                        const errorMessage =
                            response.data?.errors?.[0] ||
                            response.data?.message ||
                            `Erro ao remover etapa padrão.`;
                        return errorMessage;
                    },
                },
            }   
        )
    };

    const handleAddEtapa = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (etapaSelecionada === 0) return;

        const payloadNovaEtapaPadrao = {
                idEtapa: etapaSelecionada,
                ordem: etapas.length + 1,
                peso: 1,
                }

        await toast.promise(
            postEtapaPadrao(payloadNovaEtapaPadrao).unwrap(),
            {
                pending: "Carregando...",
                success: `Etapa configurada como padrão com sucesso!`,
                error: {
                    render({ data }) {
                        const response = data as {
                            data?: {
                            errors?: string[];
                            message?: string;
                            };
                        };

                        const errorMessage =
                            response.data?.errors?.[0] ||
                            response.data?.message ||
                            `Erro ao adicionar nova etapa padrão.`;
                        return errorMessage;
                    },
                },
            }   
        )

        setEtapaSelecionada(0);
        setOpen(false);
    };

    return (
        <>
            <Grid
                item
                xs={12}
                sx={{
                    borderRadius: "5px",
                }}
            >
                <Paper sx={{ padding: "16px" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography fontSize={20} fontWeight={600}>
                            Etapas Padrões
                        </Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
                            Adicionar Etapa
                        </Button>
                    </Box>
        
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="tabela de etapas da edição">
                        <TableHead>
                            <TableRow>
                            <TableCell sx={{ width: "50px" }}>&nbsp;</TableCell>
                            <TableCell>Ordem</TableCell>
                            <TableCell>Etapa</TableCell>
                            <TableCell>Peso</TableCell>
                            <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                            >
                            <SortableContext items={etapas.map((etapa) => etapa.idEtapa)} strategy={verticalListSortingStrategy}>
                                {etapas.map((etapa) => (
                                <SortableTableRow
                                    key={etapa.idEtapa}
                                    row={etapa}
                                    onUpdateWeight={handleUpdateWeight}
                                    onDelete={handleDelete}
                                />
                                ))}
                            </SortableContext>
                            </DndContext>
                        </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>
            <FormModal
            open={open}
            onClose={() => setOpen(false)}
            onSubmit={handleAddEtapa}
            title="Adicionar Etapa na Edição"
            description="Escolha uma etapa disponível para adicionar."
            submitLabel="Adicionar"
            isSubmitDisabled={etapaSelecionada === 0}
            emptyMessage={
                etapasSelect.length === 0
                ? "Nenhuma etapa disponível para adicionar."
                : undefined
            }
            >
                <FormControl fullWidth margin="normal">
                    <InputLabel id="etapa-label">Etapa *</InputLabel>
                    <Select
                    labelId="etapa-label"
                    id="s2-tipo-graduacao"
                    label="Etapa *"
                    value={etapaSelecionada}
                    onChange={(e) => setEtapaSelecionada(Number(e.target.value))}
                    >
                    {etapasSelect.map((etapa) => (
                        <MenuItem key={etapa.idEtapa} value={etapa.idEtapa}>
                        {etapa.nome}
                        </MenuItem>
                    ))}
                    </Select>
                </FormControl>
            </FormModal>
            <DeleteModal 
                title={"Remover etapa padrão"} 
                context={"Realmente deseja remover esta etapa das etapas padrões?"} 
                deleteFunction={() => handleConfirmDelete(idEtapaPadrao)} 
                open={openModalDelete} 
                setOpen={setOpenModalDelete}
            />
        </>
    )
}